/**
 * New revision info (api/get-newrevisioninfo). All state lives in slice; no dispatches.
 * On success we set isViewCopyRequired, isWord, selectedRevisionItem, newRevision, etc. from response.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UserAuth from "../../../auth/userAuth";
import { getNewRevisionInfo as getNewRevisionInfoAPI } from "../../api/documentsAPI";
import { serializeError } from "../../utils/serializeError";
import { DOC_CHECK_IN_TYPE } from "../../constants/metaData";
import { FILE_STATE, MANDATORY_TYPE } from "../../constants/metaData";

const userAuth = new UserAuth();

function getDocFormats(formats, viewRequired, formatMandatory, objectConnectionRequired) {
  const items = [];
  if (formats) {
    formats.forEach((item) => {
      items.push({
        id: `${item.docClass}${item.formatSize}`,
        code: `${item.formatSize}`,
        name: `${item.formatSize} - ${item.formatSizeName}`,
        parent: `${item.docClass}`,
        viewRequired,
        formatRequired: `${formatMandatory}`,
        objConRequired: `${objectConnectionRequired}`,
      });
    });
  }
  return items;
}

function getDocClasses(classes) {
  const items = [];
  try {
    if (classes) {
      classes.forEach((item) => {
        items.push({
          id: item.docClass,
          code: `${item.docClass}`,
          name: `${item.docClass} - ${item.docName}`,
          viewRequired: `${item.viewRequired}`,
          formatRequired: `${item.formatMandatory}`,
          objConRequired: `${item.objectConnectionRequired}`,
          children: getDocFormats(
            item.formats,
            item.viewRequired,
            item.formatMandatory,
            item.objectConnectionRequired
          ),
        });
      });
    }
  } catch (e) {
    // ignore
  }
  return items;
}

/**
 * @param {object} payload - { selectedRow, requestDocRevInfo, documentCheckInType, uploadFiles, uploadViewCopyCount }
 */
export const fetchNewRevisionInfoThunk = createAsyncThunk(
  "newRevisionInfo/fetch",
  async (payload, { dispatch, rejectWithValue }) => {
    const {
      selectedRow,
      requestDocRevInfo = false,
      documentCheckInType = DOC_CHECK_IN_TYPE.checkinNewRevision,
      uploadFiles = [],
      uploadViewCopyCount = 0,
      isTemplate = false,
    } = payload ?? {};
    if (!selectedRow || !selectedRow.class) {
      return rejectWithValue({ message: "Selected document required" });
    }
    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }
      const params = {
        docClass: selectedRow.class,
        docNo: selectedRow.docNo,
        docSheet: selectedRow.sheetNo ?? selectedRow.docSheet,
        docRev: selectedRow.docRev,
        requestDocRevInfo,
      };
      const res = await getNewRevisionInfoAPI(params, accessToken);
      const resData = res?.data;
      const status = res?.status;
      let applied = null;

      if (status === 200 && resData?.newRevisionBasicInfo) {
        const newRevisionBasicInfo = resData.newRevisionBasicInfo;
        const documentBasicInfo = resData.documentBasicInfo;
        const data = getDocClasses(documentBasicInfo?.classDetails);
        const fileTypeDetails = documentBasicInfo?.fileTypeDetails ?? [];

        let viewCopyRequired = MANDATORY_TYPE?.no ?? "N";
        let formatRequired = false;
        if (data?.length) {
          const found = data.find((d) => d.code === selectedRow.class);
          if (found?.viewRequired === (MANDATORY_TYPE?.yes ?? "Y")) viewCopyRequired = MANDATORY_TYPE?.yes ?? "Y";
          if (found?.formatRequired === (MANDATORY_TYPE?.yes ?? "Y") || found?.formatRequired === "true" || found?.formatRequired === true) formatRequired = true;
        }

        const isViewCopyRequired = viewCopyRequired === (MANDATORY_TYPE?.yes ?? "Y");
        const isWord = (fileTypeDetails || []).some(
          (item) => item?.fileExtention && ["DOCX", "DOC", "DOCM"].includes(String(item.fileExtention).toUpperCase())
        );
        const createViewCopyDisable = !(isViewCopyRequired && isWord);
        const createViewCopy =
          isViewCopyRequired && uploadFiles?.length > 0 ? !(uploadViewCopyCount > 0) : false;

        const docRev = requestDocRevInfo && resData.documentRevision ? resData.documentRevision : null;
        const skipSelectBecauseCheckedOut =
          !isTemplate &&
          docRev &&
          documentCheckInType === DOC_CHECK_IN_TYPE.checkinNewRevision &&
          docRev.EdmStatus === FILE_STATE?.checkedOut;
        const selectedRevisionItem = skipSelectBecauseCheckedOut
          ? null
          : {
              class: selectedRow.class,
              docNo: selectedRow.docNo,
              docRev: selectedRow.docRev,
              sheetNo: selectedRow.sheetNo ?? selectedRow.docSheet,
              title: (docRev?.Title ?? selectedRow.title ?? selectedRow.Title ?? ""),
              format: docRev?.FormatSize ?? selectedRow.format,
              documentNo: selectedRow.documentNo ?? (selectedRow.docNo != null && selectedRow.docRev != null ? `${selectedRow.docNo} - ${selectedRow.docRev}` : ""),
            };

        applied = {
          isViewCopyRequired,
          isFormatRequired: formatRequired,
          isWord,
          docClassFormatList: data,
          fileTypes: fileTypeDetails,
          createViewCopy,
          createViewCopyDisable,
          selectedRevisionItem,
          newRevision: newRevisionBasicInfo.nextRevision ?? "",
          newRevisionText: "",
          newRevisionObjCon: newRevisionBasicInfo.objectConnections ?? [],
          newRevisionDocInfo: [selectedRow],
          newRevisionDisable: false,
        };
      }
      return { data: resData, status, applied };
    } catch (err) {
      return rejectWithValue(serializeError(err));
    }
  }
);

const initialState = {
  data: null,
  isLoading: false,
  isError: false,
  message: "",
  isViewCopyRequired: false,
  isFormatRequired: false,
  isWord: false,
  docClassFormatList: [],
  fileTypes: [],
  createViewCopy: false,
  createViewCopyDisable: true,
  selectedRevisionItem: null,
  newRevision: "",
  newRevisionText: "",
  newRevisionObjCon: [],
  newRevisionDocInfo: [],
  newRevisionDisable: true,
  validationError: null,
};

const newRevisionInfoSlice = createSlice({
  name: "newRevisionInfo",
  initialState,
  reducers: {
    clearNewRevisionInfo: (state) => {
      Object.assign(state, initialState);
    },
    setNewRevisionText: (state, action) => {
      state.newRevisionText = action.payload ?? "";
    },
    setCreateViewCopy: (state, action) => {
      state.createViewCopy = !!action.payload;
    },
    setNewRevision: (state, action) => {
      state.newRevision = action.payload ?? "";
    },
    setValidationError: (state, action) => {
      state.validationError = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNewRevisionInfoThunk.pending, (state, action) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
        const row = action.meta?.arg?.selectedRow;
        if (row) {
          state.selectedRevisionItem = {
            class: row.class,
            docNo: row.docNo,
            docRev: row.docRev,
            sheetNo: row.sheetNo ?? row.docSheet,
            title: row.title ?? row.Title ?? "",
            format: row.format,
            documentNo: row.documentNo ?? (row.docNo != null && row.docRev != null ? `${row.docNo} - ${row.docRev}` : ""),
          };
        }
      })
      .addCase(fetchNewRevisionInfoThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.message = "";
        state.data = action.payload?.data ?? null;
        const applied = action.payload?.applied ?? null;
        if (applied) {
          state.isViewCopyRequired = applied.isViewCopyRequired ?? false;
          state.isFormatRequired = applied.isFormatRequired ?? false;
          state.isWord = applied.isWord ?? false;
          state.docClassFormatList = applied.docClassFormatList ?? [];
          state.fileTypes = applied.fileTypes ?? [];
          state.createViewCopy = applied.createViewCopy ?? false;
          state.createViewCopyDisable = applied.createViewCopyDisable ?? true;
          state.selectedRevisionItem = applied.selectedRevisionItem ?? state.selectedRevisionItem;
          state.newRevision = applied.newRevision ?? "";
          state.newRevisionText = applied.newRevisionText ?? "";
          state.newRevisionObjCon = applied.newRevisionObjCon ?? [];
          state.newRevisionDocInfo = applied.newRevisionDocInfo ?? [];
          state.newRevisionDisable = applied.newRevisionDisable ?? false;
        }
      })
      .addCase(fetchNewRevisionInfoThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload?.message ?? action.error?.message ?? "Failed to load new revision info.";
        state.data = null;
      });
  },
});

export const { clearNewRevisionInfo, setNewRevisionText, setCreateViewCopy, setNewRevision, setValidationError } = newRevisionInfoSlice.actions;
export default newRevisionInfoSlice.reducer;
